import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { api, errMsg, CONTENT_TYPES } from "../lib/api";
import { PageHeader, btnPrimary, inputCls } from "../components/ui-kit";
import { TaskList } from "../components/TaskList";

const NewTaskDialog = ({ open, onClose, onCreated }) => {
  const [procs, setProcs] = useState([]);
  const [f, setF] = useState({ title: "", process_id: "", class_name: "", due_date: "", description: "", start_step: 0 });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) api.get("/processes").then((r) => setProcs(r.data)); }, [open]);
  const sel = procs.find((p) => p.id === f.process_id);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.process_id) return toast.error("Choose a process");
    if (!f.due_date) return toast.error("Set a deadline");
    setBusy(true);
    try {
      await api.post("/tasks", f);
      toast.success("Task assigned and sent to the queue");
      setF({ title: "", process_id: "", class_name: "", due_date: "", description: "", start_step: 0 });
      onCreated();
    } catch (e2) {
      toast.error(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="border-[#282E3D] bg-[#141721] text-slate-100 sm:max-w-lg">
        <DialogHeader><DialogTitle className="font-heading text-2xl">Assign a new task</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <input required placeholder="Title, e.g. Mock Test 14 — Algebra" data-testid="create-task-title-input" className={inputCls} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
          <select data-testid="create-task-process-select" className={inputCls} value={f.process_id} onChange={(e) => setF({ ...f, process_id: e.target.value, start_step: 0 })}>
            <option value="">Select process…</option>
            {procs.map((p) => <option key={p.id} value={p.id}>{p.name} · {CONTENT_TYPES[p.content_type]?.label}{p.class_linked ? " · class-linked" : ""}</option>)}
          </select>
          {sel && <div className="font-mono text-[11px] text-slate-500">{sel.steps.map((s) => s.name).join(" → ")}</div>}
          {sel && (
            <label className="block">
              <span className="mb-1 block text-[11px] font-medium text-slate-400">Start from step</span>
              <select data-testid="create-task-start-step-select" className={inputCls} value={f.start_step} onChange={(e) => setF({ ...f, start_step: Number(e.target.value) })}>
                {sel.steps.map((s, i) => <option key={s.id} value={i}>Step {i + 1}: {s.name}{i === 0 ? " (from the beginning)" : i === 1 ? " (step 1 already done)" : ` (steps 1–${i} already done)`}</option>)}
              </select>
            </label>
          )}
          <div className="grid grid-cols-2 gap-3">
            <input placeholder={sel?.class_linked ? "Class / batch (required)" : "Class / batch (optional)"} required={!!sel?.class_linked} data-testid="create-task-class-input" className={inputCls} value={f.class_name} onChange={(e) => setF({ ...f, class_name: e.target.value })} />
            <label className="block">
              <span className="mb-1 block text-[11px] font-medium text-slate-400">Deadline *</span>
              <input type="date" required min={new Date().toISOString().slice(0, 10)} data-testid="create-task-due-input" className={inputCls} value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} />
            </label>
          </div>
          <textarea rows={3} placeholder="Brief / instructions" data-testid="create-task-description-input" className={inputCls} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
          <button data-testid="create-task-submit-button" disabled={busy} className={`${btnPrimary} w-full`}>{busy ? "Creating…" : "Create & queue"}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function AllTasks() {
  const [open, setOpen] = useState(false);
  const [key, setKey] = useState(0);
  const [status, setStatus] = useState("all");
  return (
    <div>
      <PageHeader eyebrow="Tasks" title="Tasks & deadlines" desc="Assign tasks to a process with a deadline, and track every task across the pipeline."
        action={<button data-testid="open-create-task-button" className={btnPrimary} onClick={() => setOpen(true)}><Plus className="h-4 w-4" /> Assign task</button>} />
      <div className="mb-6 flex flex-wrap gap-2">
        {["all", "queued", "in_progress", "overdue", "completed"].map((s) => (
          <button key={s} data-testid={`tasks-filter-${s}`} onClick={() => setStatus(s)}
            className={`rounded-full border px-4 py-1.5 text-xs font-medium capitalize transition-colors ${status === s ? "border-[#FF6B00] bg-[#FF6B00] text-white" : "border-[#282E3D] text-slate-400 hover:text-white"}`}>
            {s.replace("_", " ")}
          </button>
        ))}
      </div>
      <TaskList scope="all" refreshKey={key} filter={(t) => status === "all" || (status === "overdue" ? t.overdue : t.status === status)} emptyTitle="No tasks" emptyDesc="Assign one to a process to get things moving." />
      <NewTaskDialog open={open} onClose={() => setOpen(false)} onCreated={() => { setOpen(false); setKey((k) => k + 1); }} />
    </div>
  );
}
